import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  SearchBox,
  PageHeading,
  Loader,
  NoData,
  Toastify,
} from "atoms";
import Table from "molecules/Table";

import { AddNewRoleModal, ConfirmationModal } from "molecules/Modals";
import MemoDeleteIcon from "assets/icons/DeleteIcon";
import MemoEdit from "assets/icons/Edit";
import MemoBlock from "assets/icons/Block";
import MemoAdd from "assets/icons/Add";
import { usePropsoch } from "contexts/Propsoch";
import { format } from "date-fns";

const columns = [
  {
    title: "Role",
    field: "role",
    sortKey: "role",
    render: (row) => <>{row.role}</>,
  },
  {
    title: "Created At",
    field: "createdAt",
    sortKey: "createdAt",
    render: (row) => <>{row.createdAt}</>,
  },
  // {
  //   title: "Email",
  //   field: "email",
  //   sortKey: "email",
  //   render: (row) => <>{row.email}</>,
  // },
  // {
  //   title: "Status",
  //   field: "status",
  //   sortKey: "status",
  //   render: (row) => (
  //     <Box
  //       color="white"
  //       p="0.4rem 1.6rem"
  //       width="fit-content"
  //       borderRadius="1rem"
  //       fontSize="1.2rem"
  //       fontFamily="NeometricBold"
  //       textTransform="capitalize"
  //       bg={statusColor(row.status.toLowerCase())}
  //     >
  //       {row.status}
  //     </Box>
  //   ),
  // },

  {
    title: "Actions",
    // rightAligned: true,
    render: (row) => (
      <Grid
        gridAutoFlow="column"
        gridGap="1.8rem"
        justifyContent="start"
        alignItems="center"
        color="neutral.600"
      >
        <Box color="neutral.900" cursor="pointer">
          <MemoEdit height="1.8rem" width="1.8rem" />
        </Box>
        <Box color="neutral.900" cursor="pointer">
          <MemoBlock height="2rem" width="2rem" />
        </Box>
        <Box color="accents.100" cursor="pointer">
          <MemoDeleteIcon height="2rem" width="2rem" />
        </Box>
      </Grid>
    ),
  },
];

const Roles = () => {
  const [search, setSearch] = useState("");
  const [isModalVisible, setModalVisible] = useState(false);

  // roles crud
  const {
    state: { roles, userRole },
    actions: { getRoles, createRole, updateRole, deleteRole },
  } = usePropsoch();

  const [loading, setLoading] = useState(false);

  const fetchRoles = useCallback(() => {
    setLoading(true);
    getRoles()
      .then(() => {})
      .catch(console.error)
      .finally(() => {
        setLoading(false);
      });
  }, [getRoles]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // roles crud end
  const [deleteModal, setDeleteModal] = useState(false);

  const isAuthorisedToAdd = useMemo(() => {
    return userRole?.isSuperadmin;
  }, [userRole?.isSuperadmin]);

  const isAuthorisedToEdit = useMemo(() => {
    return userRole?.isSuperadmin;
  }, [userRole?.isSuperadmin]);

  const isAuthorisedToDelete = useMemo(() => {
    return userRole?.isSuperadmin;
  }, [userRole?.isSuperadmin]);

  return (
    <Box>
      <Flex justifyContent="space-between" pb="2.4rem">
        <PageHeading text="Roles" mt="1.8rem" />
        <Button
          variant={"primary"}
          width="fit-content"
          px="2rem"
          height="4.8rem"
          onClick={() => setModalVisible({ visible: true })}
          disabled={!isAuthorisedToAdd}
        >
          <Flex justifyContent="center" alignItems="center" gridGap="2rem">
            <MemoAdd />
            Add New Roles
          </Flex>
        </Button>
      </Flex>
      <Card>
        <SearchBox
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClose={() => setSearch("")}
          placeholder="Search Role"
          style={{ width: "30rem" }}
          removeValidationIcon={!search}
        />
        <Box mt="2.4rem" {...(loading && { minHeight: "4rem" })}>
          <Loader loading={loading}>
            <Table
              tableHeader={columns.map(({ title, rightAligned }) => ({
                name: title,
                rightAligned,
                containerProps: {
                  fontSize: "1.4rem",
                  fontWeight: "600",
                },
              }))}
              borderColor="neutral.500"
            >
              <Box as="tbody" bg="white">
                {roles
                  ?.filter((role) =>
                    role?.name?.toLowerCase()?.includes(search?.toLowerCase())
                  )
                  ?.map((role, index) => (
                    <tr key={index}>
                      <td>{role?.name}</td>
                      <td>
                        {role?.createdAt &&
                          format(new Date(role?.createdAt), "dd-MM-yyyy")}
                      </td>
                      <td>
                        <Grid
                          gridAutoFlow="column"
                          gridGap="1.8rem"
                          justifyContent="start"
                          alignItems="center"
                          color="neutral.600"
                        >
                          <Box
                            color={
                              isAuthorisedToEdit ? "neutral.900" : "neutral.300"
                            }
                            cursor={
                              isAuthorisedToEdit ? "pointer" : "not-allowed"
                            }
                            onClick={
                              isAuthorisedToEdit
                                ? () => setModalVisible({ visible: true, role })
                                : undefined
                            }
                          >
                            <MemoEdit height="1.8rem" width="1.8rem" />
                          </Box>
                          {/* <Box color="neutral.900" cursor="pointer">
                        <MemoBlock height="2rem" width="2rem" />
                      </Box> */}
                          <Box
                            color={
                              isAuthorisedToDelete
                                ? "accents.100"
                                : "neutral.300"
                            }
                            cursor={
                              isAuthorisedToDelete ? "pointer" : "not-allowed"
                            }
                            onClick={
                              isAuthorisedToDelete
                                ? () => {
                                    setDeleteModal({
                                      onClose: () => setDeleteModal(false),
                                      icon: {
                                        Icon: MemoDeleteIcon,
                                        ICprops: { color: "accents.100" },
                                      },
                                      textOne: "Are you sure you want to",
                                      textTwo: "delete",
                                      textThree: `the role ${role?.name}`,
                                      onConfirm: () => {
                                        setLoading(true);
                                        deleteRole(role?.id)
                                          ?.then(() => {
                                            Toastify(
                                              "success",
                                              `${role?.name} deleted successfully`
                                            );
                                          })
                                          ?.catch((e) => {
                                            console.error(e);
                                            Toastify(
                                              "error",
                                              `Failed to delete ${role?.name}`
                                            );
                                          })
                                          .finally(() => {
                                            fetchRoles();
                                          });
                                        setDeleteModal(false);
                                      },
                                    });
                                  }
                                : undefined
                            }
                          >
                            <MemoDeleteIcon height="2rem" width="2rem" />
                          </Box>
                        </Grid>
                      </td>
                    </tr>
                  ))}
              </Box>
            </Table>
            {roles?.filter((role) =>
              role?.name?.toLowerCase()?.includes(search?.toLowerCase())
            )?.length === 0 && <NoData />}
          </Loader>
          {/* <RcePagination
        data={data}
        current={current}
        setCurrent={setCurrent}
        showTotal={(total, range) => `${range[0]} - ${range[1]} of ${total} items`}
      /> */}
        </Box>
      </Card>
      {isModalVisible?.visible && (
        <AddNewRoleModal
          isOpen={isModalVisible?.visible}
          onClose={() => setModalVisible(false)}
          loading={isModalVisible?.loading}
          error={isModalVisible?.error}
          resetError={() =>
            setModalVisible((prev) => ({
              ...prev,
              error: "",
            }))
          }
          preRole={isModalVisible?.role}
          onSubmit={({ role }) => {
            setModalVisible((prev) => ({ ...prev, loading: true }));
            if (isModalVisible?.role) {
              updateRole(isModalVisible?.role?.id, role)
                ?.then(() => {
                  Toastify("success", `${role} updated successfully`);
                })
                ?.catch((e) => {
                  setModalVisible((prev) => ({
                    ...prev,
                    loading: false,
                    error: e?.message,
                  }));
                  Toastify("error", `Failed to update ${role}`, e?.message);
                })
                .finally(() => {
                  setModalVisible(false);
                  fetchRoles();
                });
            } else {
              createRole(role)
                ?.then(() => {
                  Toastify("success", `${role} created successfully`);
                })
                ?.catch((e) => {
                  setModalVisible((prev) => ({
                    ...prev,
                    loading: false,
                    error: e?.message,
                  }));
                  Toastify("error", `Failed to create ${role}`, e?.message);
                })
                .finally(() => {
                  setModalVisible(false);
                  fetchRoles();
                });
            }
          }}
        />
      )}
      {deleteModal && <ConfirmationModal {...deleteModal} />}
    </Box>
  );
};

export default Roles;
